/**
 * Every word the app says, in one place.
 *
 * Two reasons it lives here rather than inline in components. One: a test can
 * check the whole vocabulary at once against the language rules the house runs on
 * — describe the effort, never label the child; no irony; no "almost"; no
 * exclamation stacking. Two: the child with ASD reads these literally, so a
 * careless "¡casi!" is not a small thing.
 */

export const es = {
  app: {
    name: 'Misión Hogar',
    tagline: 'Rutinas, misiones y tiempo ganado, en casa.',
  },
  shell: {
    phaseLabel: 'Fase 0 · Cimientos',
    phaseExplain:
      'Esta pantalla comprueba que las bases funcionan. El juego, las rutinas y la tienda llegan en la fase 1.',
    systemTitle: 'Estado del sistema',
    refresh: 'Volver a comprobar',
    checking: 'Comprobando',
    loadFailed: 'No se ha podido leer el registro en este dispositivo.',
    loadFailedHint: 'Cierra la app y vuelve a abrirla. Si sigue igual, usa una copia de seguridad.',
  },
  checks: {
    installed: 'Instalada en el dispositivo',
    installedYes: 'Sí, se abre como una app',
    installedNo: 'Todavía se abre en el navegador',
    // Tres finales distintos, porque son tres situaciones distintas y antes las
    // tres decían lo mismo: «busca en el menú».
    installedCan: 'Este navegador puede instalarla',
    installedDo: 'Instalar la app',
    // No se da por instalada porque el navegador diga que sí: hay navegadores que
    // crean un acceso directo que sigue abriéndose con barra. La prueba de verdad
    // es abrirla desde la pantalla de inicio y volver a mirar esta fila.
    installedDone: 'Ábrela desde la pantalla de inicio y vuelve aquí: esta fila lo confirmará.',
    installedProof: 'Si al abrirla desde el inicio sale la barra del navegador, no está instalada.',
    installedNope: 'Este navegador no ofrece instalarla.',
    installedHint: 'En Chrome para Android: menú de tres puntos, «Instalar app».',
    installedIos: 'En iPhone y iPad: botón de compartir, «Añadir a pantalla de inicio».',
    offline: 'Funciona sin conexión',
    offlineYes: 'Sí, los archivos están guardados',
    offlineNo: 'Todavía no se han guardado los archivos',
    offlineHint: 'Se guardan solos la primera vez que se abre con conexión.',
    storage: 'Almacenamiento protegido',
    storageYes: 'Sí, el navegador no lo borrará solo',
    storageNo: 'El navegador podría borrarlo si falta espacio',
    storageHint: 'Haz una copia de seguridad cada semana hasta la fase 3.',
    connection: 'Conexión ahora',
    online: 'Con conexión',
    offlineNow: 'Sin conexión',
    log: 'Registro de eventos',
    logCount: (count: number) => (count === 1 ? '1 evento guardado' : `${count} eventos guardados`),
    chainOk: 'Sin huecos ni cambios',
    chainBroken: (seq: number) => `Revisar: algo cambió en el evento ${seq}`,
    // La explicación del juego de mates. Falla en silencio desde el punto de
    // vista del adulto —el niño solo ve «ahora no se puede»—, así que aquí se
    // puede probar y ver por qué.
    maestro: 'El maestro que explica',
    maestroSinServidor: 'Esta instalación no tiene servidor detrás.',
    maestroSinClave: 'Falta la clave en esta tablet.',
    maestroSinClaveHint:
      'Se pone en modo padres, en el ayudante. Es de esta tablet y de este sitio.',
    maestroProbar: 'Probar',
    maestroProbando: 'Probando',
    maestroVale: 'Funciona: ha explicado una cuenta.',
    maestroSinProbar: 'Sin probar todavía.',
    version: 'Versión instalada',
    versionHint: 'Si dos tablets no se comportan igual, compara esto en las dos.',
    device: 'Este dispositivo',
    pin: 'PIN de padres',
    pinSet: 'Configurado',
    pinMissing: 'Sin configurar',
  },
  pin: {
    title: 'PIN de padres',
    explain: 'Cuatro a ocho números. Abre el modo padres. Se puede cambiar cuando queráis.',
    create: 'Crear el PIN',
    createField: 'Escribe el PIN nuevo',
    repeat: 'Repite el PIN',
    save: 'Guardar el PIN',
    change: 'Cambiar el PIN',
    enter: 'Escribe el PIN',
    open: 'Abrir modo padres',
    submit: 'Entrar',
    cancel: 'Cancelar',
    saved: 'PIN guardado',
    opened: 'Modo padres abierto',
    close: 'Cerrar modo padres',
    errorShort: 'El PIN necesita entre cuatro y ocho números.',
    errorDigits: 'El PIN solo puede llevar números.',
    errorMatch: 'Los dos PIN no son iguales. Escríbelos otra vez.',
    errorWrong: 'Ese PIN no es correcto.',
    lockedFor: (seconds: number) =>
      seconds === 1
        ? 'Espera 1 segundo para volver a intentarlo.'
        : `Espera ${seconds} segundos para volver a intentarlo.`,
  },
  update: {
    available: 'Hay una versión nueva preparada.',
    explain: 'Se instala cuando tú digas, nunca a mitad de una rutina.',
    apply: 'Instalar ahora',
    later: 'Más tarde',
  },
  privacy: {
    title: 'Dónde se guardan estos datos',
    body: 'Todo se queda en esta tablet. No se envía nada a ningún servidor.',
    consequence: 'Nadie más puede verlo. Tampoco hay copia automática.',
    backup: 'Conviene exportar el registro una vez por semana desde el modo padres.',
    understood: 'Entendido',
  },
  backup: {
    title: 'Copia de seguridad',
    explain:
      'Guarda toda la historia en un archivo. Las monedas y las rachas salen de ella, así que al importarla vuelve todo.',
    fuera: 'Los PIN no van dentro. En una tablet nueva hay que ponerlos otra vez.',
    export: 'Exportar el registro',
    exported: (count: number) => `Se han exportado ${count} eventos.`,
    importing: 'Importar un registro',
    imported: (imported: number, skipped: number) =>
      `Se han añadido ${imported} eventos. Ya había ${skipped}.`,
    sinRegistro: 'Ese archivo no tiene un registro dentro.',
    // El aviso que faltaba, y que costó dos hijos duplicados que no se podían
    // quitar de ningún sitio.
    yaHayCasa: 'Esta tablet ya tiene una casa dentro.',
    yaHayCasaExplica:
      'Si la copia es de otra casa, las dos quedan dentro y verás los hijos repetidos.',
    yaHayCasaSigo: 'Es una copia de esta casa: importar',
    yaHayCasaNo: 'Dejarlo',
    fotosTitle: 'Las fotos',
    fotosExplain:
      'Van en su propio archivo porque pesan. El registro se puede exportar cada semana; las fotos, de vez en cuando.',
    fotosExport: 'Exportar las fotos',
    fotosImport: 'Importar las fotos',
    fotosVacio: 'Todavía no hay ninguna foto guardada.',
    fotosPreparando: 'Preparando el archivo. Puede tardar unos segundos.',
    fotosExported: (cuantas: number, tamaño: string) =>
      cuantas === 1
        ? `Se ha exportado una foto. El archivo pesa ${tamaño}.`
        : `Se han exportado ${cuantas} fotos. El archivo pesa ${tamaño}.`,
    fotosImported: (puestas: number, yaEstaban: number) => {
      const añadidas =
        puestas === 0
          ? 'No se ha añadido ninguna foto nueva.'
          : puestas === 1
            ? 'Se ha añadido una foto.'
            : `Se han añadido ${puestas} fotos.`
      if (yaEstaban === 0) return añadidas
      return `${añadidas} ${yaEstaban === 1 ? 'Ya estaba una.' : `Ya estaban ${yaEstaban}.`}`
    },
    fotosFaltan: (perdidas: number) =>
      perdidas === 1 ? 'Faltaba una en el archivo.' : `Faltaban ${perdidas} en el archivo.`,
    fotosIlegible: 'Ese archivo no se puede leer como una copia de las fotos.',
  },
  setup: {
    title: 'Vamos a preparar la casa',
    explain: 'Se hace una sola vez. Todo esto se puede cambiar después en la reunión.',
    // Lo primero de la pantalla: quien viene de otra tablet no tiene que crear
    // nada, y crear primero es justo lo que deja dos casas dentro del registro.
    yaTengo: '¿Vienes de otra tablet?',
    yaTengoExplain: 'Si tienes una copia de seguridad, impórtala ahora y sigue por donde ibas.',
    yaTengoBoton: 'Importar una copia',
    yaTengoHecho: (puestos: number) => `Listo: ${puestos} eventos. Ya puedes seguir.`,
    yaTengoNada: 'Ese archivo no tiene un registro dentro.',
    oEmpezar: 'O empezar de cero',
    adults: 'Los adultos de la casa',
    adultsExplain: 'Los que pueden validar y configurar. Puede ser uno o los que seáis.',
    adultName: (n: number) => `Nombre del adulto ${n}`,
    addAdult: 'Añadir otro adulto',
    removeAdult: 'Quitar este adulto',
    children: 'Los hijos',
    addChild: 'Añadir otro hijo',
    removeChild: 'Quitar este hijo',
    childNumber: (n: number) => `Hijo ${n}`,
    // Numerado y no «mayor / pequeño»: con tres hijos, el tercero también era «el
    // pequeño». Y el nombre del adulto ya no se pregunta de uno en uno.
    childName: (n: number) => `Nombre del hijo ${n}`,
    childYear: 'Año de nacimiento',
    literal: 'Lenguaje literal y sin sorpresas',
    literalHelp: 'Sin ironía, cambios avisados con antelación y cofres que enseñan lo que llevan.',
    glutenFree: 'Sin gluten',
    glutenFreeHelp: 'Añade las misiones de leer etiquetas y preparar su desayuno seguro.',
    create: 'Crear la casa',
    creating: 'Creando',
    ready: 'Ya está preparada.',
  },
  who: {
    title: '¿Quién eres?',
    parents: 'Modo padres',
    // Cuando hay más de un adulto: lo que se valide queda a nombre de quien
    // conteste, y no de la primera persona que se dio de alta.
    cualAdulto: '¿Quién está con la tablet?',
    cualAdultoHelp: 'Lo que valides queda a tu nombre.',
  },
  childPin: {
    explain: 'Escribe tu número para entrar en tu pantalla.',
    enter: 'Tu número',
    submit: 'Entrar',
    cancel: 'No soy yo',
    wrong: 'Ese número no es. Prueba otra vez.',
    // Lo que ve el padre en los ajustes.
    title: 'Su número secreto',
    help: 'Sirve para que su hermano no entre en su pantalla. Cuatro cifras.',
    save: 'Guardar el número',
    set: 'Poner un número',
    change: 'Cambiar el número',
    clear: 'Quitar el número',
    none: 'Ahora mismo entra sin número.',
    active: 'Ahora mismo pide un número para entrar.',
    saved: 'Guardado',
    removed: 'Quitado',
    tooShort: 'Tienen que ser cuatro cifras como mínimo.',
  },
  semaforo: {
    title: 'Momento de silencio',
    open: 'Momento de silencio',
    // Lo que ve el niño.
    verde: 'Bien así',
    ambar: 'Se está oyendo',
    rojo: 'Demasiado alto',
    verdeHelp: 'El ruido está por debajo de la raya.',
    ambarHelp: 'Estáis en la raya. Un poco más bajo.',
    rojoHelp: 'Ha pasado de la raya. Bajad la voz.',
    left: (m: number, s: string): string => `Quedan ${m} minutos y ${s} segundos`,
    lastMinute: (s: string): string => `Queda menos de un minuto: ${s} segundos`,
    done: 'Se acabó el momento de silencio.',
    stop: 'Terminar',
    // Lo que ve el adulto al prepararlo.
    setup: 'Preparar el momento de silencio',
    minutes: '¿Cuántos minutos?',
    limit: 'Dónde está la raya',
    limitHelp:
      'Mueve la raya mientras hablan a un volumen que te parezca bien. La barra enseña el ruido de ahora.',
    now: 'Ruido de ahora',
    start: 'Empezar',
    privacy: 'El micrófono mide el volumen y tira la medida. No graba, no guarda y no envía nada.',
    noMic: 'Esta tablet no deja usar el micrófono. Sin él no hay semáforo.',
    noPermission: 'Hace falta dar permiso al micrófono. Está en los ajustes del navegador.',
    neverPunishes: 'El semáforo no quita monedas. Solo enseña cómo está la casa.',
  },
  avisos: {
    title: 'Avisos',
    intro: 'Esta prueba hay que hacerla en la tablet, no en un ordenador.',
    intro2: 'De su resultado depende si la app puede avisar con la app cerrada.',
    permiso: 'Permiso',
    permisos: {
      granted: 'Concedido',
      denied: 'Denegado en el navegador',
      default: 'Sin pedir todavía',
      'no-disponible': 'Este navegador no tiene avisos',
    },
    worker: 'Trabajador en segundo plano',
    sinWorkerPista: 'Hace falta instalar la app en la pantalla de inicio.',
    programables: 'Avisos con hora, sin red',
    programablesPista:
      'Si esto dijera que sí, la tablet podría avisar sola y en modo avión. Es lo mejor que podría pasar.',
    push: 'Avisos desde el servidor',
    pushPista: 'Funcionan con la app cerrada, pero la tablet necesita red en ese momento.',
    suscrita: 'Esta tablet está apuntada',
    veredicto: 'Qué camino queda',
    caminoLocal: 'La tablet puede avisar sola',
    caminoLocalPista: 'Sin servidor y sin red. Prueba el aviso de un minuto y cierra la app.',
    caminoPush: 'Hace falta que avise el servidor',
    caminoPushPista:
      'Se puede hacer con lo que ya hay montado. La tablet tendrá que tener red a esa hora.',
    caminoNativo: 'Haría falta empaquetar la app para Android',
    caminoNativoPista: 'Es lo más fiable y también lo que más trabajo lleva.',
    // Configurar las horas.
    horas: 'A qué hora avisa',
    horasHelp: 'Las horas se ponen en los ajustes de cada hijo. Aquí solo se apunta la tablet.',
    apuntar: 'Apuntar esta tablet a los avisos',
    sinHoras: 'Todavía no hay horas puestas en los ajustes de ningún hijo.',
    addHora: 'Añadir una hora',
    removeHora: 'Quitar esta hora',
    previo: 'Avisar diez minutos antes',
    previoHelp: 'Un aviso de que va a tocar, para que el cambio no llegue de golpe.',
    guardar: 'Guardar los avisos',
    guardado: (n: number): string =>
      n === 0 ? 'Avisos quitados.' : n === 1 ? 'Un aviso guardado.' : `${n} avisos guardados.`,
    necesitaRed: 'La tablet tiene que tener red a esa hora. En modo avión no llega.',
    fallos: {
      'sin-permiso': 'Sin permiso no hay avisos. Se cambia en los ajustes del navegador.',
      'sin-soporte': 'Falta instalar la app en la pantalla de inicio.',
      'sin-servidor': 'El servidor no tiene los avisos preparados.',
      'sin-red': 'No se ha podido guardar. Prueba con red.',
    },
    probar: 'Probar un aviso ahora',
    probarProgramado: 'Probar uno en un minuto',
    pruebaTitulo: 'Misión Hogar',
    pruebaCuerpo: 'Si ves esto, los avisos funcionan en esta tablet.',
    programadoCuerpo: 'Este llegó programado. Cierra la app y espera un minuto.',
    mandado: 'Mandado. Si no lo ves, míralo en los ajustes de Android de la app.',
    programado: 'Programado. Cierra la app del todo y espera un minuto.',
    denegado: 'Sin permiso no hay avisos. Se cambia en los ajustes del navegador.',
    sinSoporte: 'Este navegador no tiene avisos.',
    sinWorker: 'Falta instalar la app en la pantalla de inicio.',
    fallo: 'No se ha podido. Mira la consola si quieres el detalle.',
  },
  pendiente: {
    title: (name: string): string => `${name}: sin hacer`,
    tap: 'Toca aquí para empezar.',
    andMore: (n: number): string => (n === 1 ? 'Y otra más.' : `Y otras ${n} más.`),
    whose: 'Esto está sin hacer:',
  },
  today: {
    morning: 'Rutina de la mañana',
    afternoon: 'Rutina de la tarde',
    evening: 'Rutina de la noche',
    nowLabel: 'Ahora toca',
    missions: 'Misiones de hoy',
    noMissions: 'Hoy no quedan misiones.',
    coins: 'monedas',
    xp: 'experiencia',
    level: (n: number) => `Nivel ${n}`,
    chest: 'El cofre del día',
    chestReady: 'El cofre está listo.',
    chestOpen: 'Abrir el cofre',
    chestOpened: (min: number) => `Hoy tienes ${min} minutos de tablet.`,
    chestNoScreen: 'Hoy no hay tablet. Mañana empieza de nuevo.',
    stepsDone: (done: number, total: number) => `${done} de ${total} pasos`,
    waiting: 'Esperando a que un adulto lo mire',
    validated: 'Hecho y comprobado',
    returned: 'Falta algo. Ábrela para verlo.',
    back: 'Volver',
    onlyOne: 'Ahora, solo esto',
    nextAfter: (name: string) => `Después: ${name}`,
    nothingLeft: 'Ya está todo lo de hoy.',
    seeAll: 'Ver todo',
    seeOne: 'Ver solo una cosa',
    anotherMission: 'Otra misión',
    noMissionNow: 'Ahora no',
    agreement: 'Esto es lo que acordamos',
    changedToday: 'Esta tarea ha cambiado hoy. Léela otra vez antes de empezar.',
    shop: 'Tienda',
    timer: 'Tiempo',
    juegos: 'Juegos',
  },
  juegos: {
    title: 'Juegos',
    explain: 'Para practicar. Puedes jugar siempre que quieras, tantas veces como quieras.',
    mates: 'Tablas y divisiones',
    matesExplain: 'Sumar, restar, multiplicar y dividir. Insiste en lo que te cuesta a ti.',
    hasta: (curso: number) => `Ahora mismo, lo de ${curso}º de primaria y lo de antes.`,
    mision: (coins: number, word: string) => `Hoy cuenta como misión: ${coins} ${word}.`,
    misionHecha: 'La misión de hoy ya la has hecho. Puedes seguir jugando.',
    jugar: 'Jugar',
    back: 'Volver',
  },
  mates: {
    title: 'Tablas y divisiones',
    // Lo primero: qué quiere repasar. Lo pidió Gabriel, para poder ponerse
    // solo con lo que no le sale.
    elegirTitulo: '¿Qué quieres repasar?',
    familia: {
      suma: 'Sumas',
      resta: 'Restas',
      multiplicacion: 'Multiplicaciones',
      division: 'Divisiones',
      todo: 'De todo',
    },
    vaPor: (n: number, de: number) => `Pregunta ${n} de ${de}`,
    tuRespuesta: 'Tu respuesta',
    borrar: 'Borrar',
    comprobar: 'Comprobar',
    siguiente: 'Siguiente',
    // Tras contestar. Ni ironía ni «casi»: se dice qué es y se sigue.
    correcto: 'Correcto',
    noEsEsa: 'No es esa',
    esta: (pide: string, solucion: string) => `${pide} = ${solucion}`,
    dejarlo: 'Dejarlo por ahora',
    // La cuenta como en el colegio, paso a paso. Tres maneras de verla: hecha
    // entera (el ejemplo), con los últimos pasos para el niño (la ayuda que se
    // va retirando), y a petición en cualquier momento.
    ayuda: 'Hacerla paso a paso',
    verComoSeHace: 'Ver cómo se hace',
    ejemploTitulo: 'Mira cómo se hace',
    ejemploLuego: 'Después te toca a ti una parecida.',
    pasoDe: (n: number, de: number) => `Paso ${n} de ${de}`,
    sigo: 'Sigo',
    ahoraTu: 'Ahora tú',
    pista: 'Pista',
    otraPista: 'Otra pista',
    pasoBien: 'Eso es.',
    pasoNoEsEsa: 'No es esa. Mira la pista.',
    pasoTeLoDigo: (respuesta: string) => `Es ${respuesta}. Lo escribo y seguimos.`,
    teToca: 'Te toca a ti',
    hechaConPasosBien: 'Hecha paso a paso, sin fallar ninguno.',
    hechaConPasosFallos: (n: number) =>
      n === 1
        ? 'Hecha paso a paso. Uno te lo he tenido que decir.'
        : `Hecha paso a paso. ${n} te los he tenido que decir.`,
    // La explicación del maestro, con la IA. Es un extra: si no hay red, el
    // juego sigue igual y esto ni aparece. Ahora dice lo mismo que los pasos,
    // con otras palabras, por si las de aquí no se entienden.
    explicar: 'Dímelo con otras palabras',
    explicando: 'Pensando cómo decírtelo',
    explicacionTitulo: 'Con otras palabras',
    sinExplicacion: 'Ahora no se puede explicar. La cuenta sigue siendo la de arriba.',
    // El final.
    finTitulo: 'Repaso terminado',
    finCuenta: (aciertos: number, total: number) => `Has acertado ${aciertos} de ${total}.`,
    finLentas: (n: number) =>
      n === 1 ? 'Una te ha costado más tiempo.' : `${n} te han costado más tiempo.`,
    finConAyuda: (n: number) =>
      n === 1 ? 'Una la has hecho paso a paso.' : `${n} las has hecho paso a paso.`,
    finPractica: (que: string) => `Para la próxima vez: ${que}.`,
    finGanado: (coins: number, word: string) => `Misión hecha: ${coins} ${word}.`,
    finYaEstaba: 'La misión de hoy ya estaba hecha. Esta sesión cuenta para aprender.',
    finAMedias: 'Lo dejamos aquí. Queda guardado lo que has contestado.',
    otraVez: 'Otra sesión',
    terminar: 'Terminar',
  },
  task: {
    steps: 'Pasos',
    reference: 'Así queda cuando está hecho',
    markDone: 'Ya está',
    allSteps: (n: number): string => `Ver los ${n} pasos`,
    onlyNext: 'Ver solo el que toca',
    missingStep: (step: string): string => `Antes de eso te falta: ${step}`,
    undoStep: 'Tocar otra vez para quitarlo',
    unmark: 'Quitar la marca',
    marked: 'Marcada. Un adulto la comprobará.',
    takePhoto: 'Hacer una foto',
    photoTaken: 'Foto hecha',
    retakePhoto: 'Hacer otra foto',
    pays: (coins: number, xp: number, word: string) =>
      coins > 0 ? `Da ${coins} ${word} y ${xp} de experiencia` : `Da ${xp} de experiencia`,
    rung: (n: number) => `Peldaño ${n}`,
  },
  shop: {
    title: 'Tienda',
    balance: (coins: number, word: string) => `Tienes ${coins} ${word}`,
    buy: 'Comprar',
    missing: (n: number) => `Te faltan ${n}`,
    bought: 'Comprado. Un adulto te lo dará.',
    pendingTitle: 'Esperando a un adulto',
    empty: 'Todavía no hay nada en la tienda.',
    left: (n: number) => `Si lo compras te quedan ${n}`,
    think: 'Pensarlo',
    unthink: 'Quitarlo de la lista',
    planTitle: 'Lo que estoy pensando',
    planHelp: 'Aquí no se gasta nada. Es para ver cuánto sale.',
    planCost: (n: number) => `Todo junto cuesta ${n}`,
    planLeft: (n: number) => `Te quedarían ${n}`,
    planMissing: (n: number) => `Te faltan ${n} para todo esto`,
    planClear: 'Vaciar la lista',
    undo: 'Me he arrepentido',
    undone: 'Deshecho. Te devuelvo las monedas.',
  },
  timer: {
    title: 'Tiempo de tablet',
    start: 'Empezar',
    remaining: 'Queda',
    finished: 'Se acabó el tiempo.',
    lastGame: 'Última partida: tres minutos para guardar.',
    closeNow: 'He cerrado',
    notStarted: 'El tiempo empieza cuando tú digas.',
    minutes: (n: number) => (n === 1 ? '1 minuto' : `${n} minutos`),
  },
  config: {
    title: 'Ajustes de cada hijo',
    oneThing: 'Una cosa a la vez',
    oneThingHelp: 'La pantalla enseña solo la tarea que toca. Nada más.',
    literal: 'Lenguaje literal y sin sorpresas',
    largeText: 'Texto grande',
    reducedMotion: 'Sin animaciones',
    photos: 'Fotos de «así queda hecho»',
    photosHelp:
      'Una foto por tarea quita la discusión de en medio: ya no es tu palabra contra la suya.',
    // Sin el nombre: el botón está dentro de la ficha de ese hijo, que ya lo
    // encabeza. La confirmación sí lo dice, que es donde importa no equivocarse.
    // La gente de la casa: añadir y quitar después del primer día.
    gente: 'Quién vive en esta casa',
    adultos: 'Los adultos',
    adultosHelp: 'Los que pueden validar y configurar. Cada uno entra como él mismo.',
    nuevoAdulto: 'Nombre del adulto',
    anadirAdulto: 'Añadir un adulto',
    nuevoHijo: 'Añadir un hijo',
    nuevoHijoHelp: 'Llega con sus rutinas y sus misiones puestas, y con la tienda abierta.',
    anadirHijo: 'Añadir a la casa',
    quitar: 'Quitar de las listas',
    quitarSeguro: (name: string) => `¿Quitar a ${name} de las listas?`,
    quitarHelp: 'Deja de salir en la app. No se borra nada: su historia y sus monedas se quedan.',
    quitarSi: 'Sí, quitarlo',
    quitarNo: 'Dejarlo como está',
    quitarRazon: 'Quitado desde los ajustes',
    quitados: 'Quitados de las listas',
    volverAPoner: (name: string) => `Volver a poner a ${name}`,
    juego: 'El repaso de mates',
    juegoHelp:
      'Si cuenta como misión, se valida sola al terminar la sesión: nadie tiene que comprobarla.',
    juegoCoins: 'Monedas por el repaso',
    juegoCreate: 'Que cuente como misión',
    juegoActive: (coins: number, word: string) => `Cuenta como misión: ${coins} ${word} al día.`,
    juegoRemove: 'Que deje de contar',
    juegoRemoveReason: 'El repaso de mates deja de ser misión',
    tasks: 'Tareas',
    edit: 'Editar',
    close: 'Cerrar',
    name: 'Nombre',
    emoji: 'Dibujo',
    steps: 'Pasos',
    addStep: 'Añadir un paso',
    removeStep: 'Quitar este paso',
    up: 'Subir',
    down: 'Bajar',
    save: 'Guardar',
    saved: 'Guardado',
    splitHint: 'Si se para siempre en el mismo paso, pártelo en dos. Suelen ser tres cosas en una.',
    archive: 'Quitar esta tarea',
    archiveReason: '¿Por qué la quitas?',
    archived: 'Tareas quitadas',
    restore: 'Volver a ponerla',
    maxSteps: 'Ocho pasos como máximo.',
    recordar: 'A qué hora se le recuerda',
    recordarHelp:
      'Pasada esa hora, la app enseña un aviso fijo mientras la rutina siga sin hacer. Se va al terminarla.',
    recordarAdd: 'Añadir una hora',
    recordarQuitar: 'Quitar esta hora',
    recordarPrevio: 'Avisar diez minutos antes',
    recordarNinguna: 'Sin horas puestas: no se le recuerda nada.',
    recordarSave: 'Guardar las horas',
    pays: 'Lo que da',
    paysCoins: 'Monedas',
    paysXp: 'Experiencia',
    paysRoutine: 'Una rutina no da monedas. Solo experiencia.',
    look: 'Su mundo',
    lookHelp:
      'El dibujo, el color y cómo llama a sus monedas. Si le apasiona algo, ponlo aquí: es el motivador más fuerte que hay.',
    lookEmoji: 'Su dibujo',
    lookColor: 'Su color',
    lookCoinWord: 'Cómo llama a sus monedas',
    lookCoinWordHelp: 'Déjalo vacío y se llaman monedas.',
    lookSave: 'Guardar su mundo',
    setPhoto: 'Poner la foto',
    changePhoto: 'Cambiar la foto',
    hasPhoto: 'Tiene foto',
    noPhoto: 'Sin foto',
  },
  assistant: {
    title: 'Configurar hablando',
    open: 'Configurar hablando',
    intro: 'Cuenta cómo son vuestras mañanas, vuestras tardes y vuestras noches.',
    intro2: 'El ayudante propone cambios. Tú decides cuáles entran.',
    privacyTitle: 'Esto es lo único de la app que sale de la tablet.',
    privacySends:
      'Se envían tus frases, los nombres de pila, los nombres de las tareas con sus pasos y los precios.',
    privacyKeeps: 'No salen las fotos, ni el historial, ni las monedas de nadie.',
    where: 'Va al servidor de Misión Hogar, y de ahí al ordenador de casa.',
    field: 'Cuéntalo con tus palabras',
    placeholder:
      'Por las mañanas vamos con prisa y lo que peor llevamos es el desayuno. Por la noche querría que dejaran la ropa lista.',
    send: 'Pedir propuestas',
    dictate: 'Dictarlo',
    recording: 'Grabando. Habla y luego pulsa «Ya está».',
    stopDictating: 'Ya está',
    cancelDictating: 'Tirarlo',
    transcribing: 'Pasándolo a texto.',
    dictated: 'Ahí lo tienes. Léelo y corrígelo antes de pedir propuestas.',
    dictateWhere: 'Al dictar, el audio sale hacia tu servidor y de ahí al ordenador de casa.',
    dictateKeeps: 'No se graba en ningún sitio: se pasa a texto y se suelta.',
    dictateFailures: {
      'sin-permiso': 'Hace falta dar permiso al micrófono.',
      'sin-microfono': 'Esta tablet no deja grabar audio.',
      'sin-conexion': 'No se ha podido pasar el audio a texto. Prueba otra vez.',
      'demasiado-largo': 'Ha salido muy largo. Prueba a contarlo en dos veces.',
      'no-se-entendio': 'No se ha entendido nada. Prueba a hablar más cerca.',
    },
    working: 'Pensando. Suele tardar unos segundos.',
    cancel: 'Dejarlo',
    proposals: 'Lo que propone',
    empty: 'No ha propuesto ningún cambio.',
    apply: (n: number): string => (n === 1 ? 'Aplicar 1 cambio' : `Aplicar ${n} cambios`),
    applied: 'Aplicado',
    discard: 'Descartar todo',
    keep: 'Incluir este cambio',
    rejectedTitle: 'Estas no se pueden hacer',
    rejectedHelp: 'Las reglas de la casa las decide la app, no el ayudante.',
    token: 'Clave del ayudante',
    tokenHelp: 'Se guarda solo en esta tablet.',
    saveToken: 'Guardar la clave',
    review: 'Léelo antes de aplicarlo. Lo que apliques lo verán tus hijos.',
    failures: {
      'no-configurado': 'Esta instalación no tiene ayudante configurado.',
      'sin-conexion': 'No hay conexión con el servidor. Vuelve a intentarlo luego.',
      'sin-modelo': 'El ayudante no está disponible ahora mismo. Inténtalo más tarde.',
      'tardo-demasiado': 'Ha tardado demasiado. Prueba a contarlo más corto.',
      'respuesta-ilegible': 'La respuesta no se ha entendido. Prueba a contarlo de otra forma.',
      rechazado: 'El ayudante ha rechazado la petición. Revisa la clave.',
    },
    ops: {
      'create-task': (child: string, name: string): string => `Crear «${name}» para ${child}`,
      'rename-task': (child: string, from: string, to: string): string =>
        `Renombrar «${from}» de ${child} como «${to}»`,
      'set-steps': (child: string, name: string, n: number): string =>
        `Dejar «${name}» de ${child} con ${n} ${n === 1 ? 'paso' : 'pasos'}`,
      'remove-task': (child: string, name: string): string => `Quitar «${name}» de ${child}`,
      'create-reward': (name: string, price: number): string =>
        `Poner «${name}» en la tienda por ${price} monedas`,
      'set-price': (name: string, price: number, day: string): string =>
        `«${name}» pasa a ${price} monedas el ${day}`,
    },
  },
  newTask: {
    open: 'Añadir una tarea',
    title: 'Una tarea nueva',
    kind: '¿Qué es?',
    routine: 'Rutina',
    mission: 'Misión',
    kindHelp: 'Una rutina es lo que se hace todos los días y no da monedas. Una misión sí.',
    moment: '¿Cuándo?',
    morning: 'Por la mañana',
    afternoon: 'Por la tarde',
    evening: 'Por la noche',
    coins: 'Monedas que da',
    create: 'Crear la tarea',
    created: 'Creada',
    needName: 'Ponle un nombre.',
  },
  shopEditor: {
    title: 'La tienda',
    help: 'Lo que pueden comprar con las monedas. Los precios cambian en la reunión.',
    open: 'Cambiar la tienda',
    name: 'Qué es',
    price: 'Cuánto cuesta',
    kind: 'De qué tipo',
    kinds: {
      screen: 'Tiempo de pantalla',
      privilege: 'Un permiso',
      family: 'Algo en familia',
      social: 'Con amigos',
      big: 'Algo grande',
    },
    who: '¿Para quién?',
    add: 'Añadir a la tienda',
    added: 'Añadido',
    newPrice: 'Precio nuevo',
    reprice: 'Cambiar el precio',
    repriced: (day: string): string => `Cambia el ${day}`,
    repriceHelp:
      'El precio nuevo entra en la reunión, no hoy. Así nadie se queda a medias de lo que ahorraba.',
    remove: 'Quitar de la tienda',
    removeReason: '¿Por qué la quitas?',
    removed: 'Quitada',
    archived: 'Fuera de la tienda',
    needName: 'Ponle un nombre.',
    needWho: 'Elige al menos a uno.',
    empty: 'La tienda está vacía.',
  },
  parent: {
    title: 'Modo padres',
    shop: 'La tienda',
    porQue: 'Qué es esto y cómo explicarlo',
    calendario: 'El calendario del curso',
    semaforo: 'Momento de silencio',
    assistant: 'Configurar hablando',
    validate: 'Por comprobar',
    validateEmpty: 'No hay nada por comprobar.',
    approve: 'Está bien',
    undo: 'Me he equivocado',
    undoReason: '¿Qué ha pasado?',
    undoExplain: 'Devuelve las monedas de esa comprobación y la deja por comprobar otra vez.',
    checked: 'Comprobadas hoy',
    checkedEmpty: 'Todavía no has comprobado nada hoy.',
    pays: (coins: number): string => (coins > 0 ? `Da ${coins} monedas` : 'No da monedas'),
    markedAt: (hora: string): string => `Marcada a las ${hora}`,
    reference: 'Como acordamos',
    result: 'Como ha quedado',
    reject: 'Falta un paso',
    rejectNote: '¿Qué falta?',
    grants: 'Minutos por conceder',
    grantsEmpty: 'No hay minutos pendientes.',
    grantsHelp: 'Concédelos en Family Link y luego marca aquí que están dados.',
    granted: 'Ya están dados',
    deliver: 'Entregado',
    settings: 'Ajustes del día',
    pauseDay: 'Pausar el día',
    pauseHelp: 'Un día pausado no rompe nada. Ni suma ni resta.',
    dayKind: 'Qué día es hoy',
    insights: 'Cómo va cada uno',
    config: 'Ajustes de cada hijo',
    system: 'Estado del sistema',
    exit: 'Salir del modo padres',
  },
  insights: {
    mates: 'Tablas y divisiones',
    matesCuenta: (aciertos: number, total: number, lentos: number) =>
      `${aciertos} de ${total} acertadas. ${lentos} costaron más tiempo de lo suyo.`,
    matesFlojo: (que: string, fallos: number, lentos: number) =>
      `${que}: ${fallos} sin acertar y ${lentos} que le cuestan`,
    matesLimpio: 'No hay ninguna tabla que se le atasque ahora mismo.',
    matesConAyuda: (n: number) =>
      n === 1
        ? 'Una hecha paso a paso, con la cuenta delante.'
        : `${n} hechas paso a paso, con la cuenta delante.`,
    title: 'Cómo va cada uno',
    window: (n: number) => `Últimos ${n} días`,
    active: (n: number) => (n === 1 ? 'Un día con actividad' : `${n} días con actividad`),
    afternoon: (n: number) => `Rutina de la tarde completa: ${n}`,
    started: 'Empezadas',
    finished: 'Terminadas',
    abandoned: 'Dejadas a medias',
    stuck: (text: string, position: number, times: number) =>
      `Se para antes del paso ${position}, «${text}». Ha pasado ${times} veces.`,
    takes: (minutes: number) => `Suele tardar ${minutes} minutos`,
    hour: (hour: number) => `Suele empezarla sobre las ${hour}`,
    nothing: 'Todavía no hay suficiente para decir nada.',
    allGood: 'No deja ninguna a medias.',
    readMore:
      'Un paso que se repite aquí suele ser un paso demasiado grande. Pártelo en dos en la reunión.',
  },
  calendario: {
    title: 'El calendario del curso',
    open: 'Subir el calendario del curso',
    intro: 'Sube el fichero .ics del colegio o de la Junta y se marcan los días de una vez.',
    privacidad: 'El fichero se lee aquí, en la tablet. No sube a ningún sitio.',
    elegir: 'Elegir el fichero',
    vacio: 'Ese fichero no tiene fechas que se puedan leer.',
    encontrado: (n: number): string =>
      n === 1 ? 'Ha encontrado una fecha' : `Ha encontrado ${n} fechas`,
    revisa:
      'Lo que no reconoce llega fuera, a propósito: un día de colegio marcado como festivo da tablet de más y no lo nota nadie.',
    rango: (desde: string, hasta: string): string => `Del ${desde} al ${hasta}`,
    fuera: 'Dejarlo fuera',
    aplicar: (n: number): string =>
      n === 0 ? 'Elige alguno' : n === 1 ? 'Poner esta fecha' : `Poner estas ${n} fechas`,
    // Poner un tramo a mano: el verano, el inicio y el fin de curso.
    manoTitulo: 'Poner un tramo a mano',
    manoAyuda:
      'Para lo que el fichero no trae: el verano, el fin de curso, un puente. Los fines de semana ya salen solos.',
    manoDesde: 'Desde',
    manoHasta: 'Hasta',
    manoNombre: 'Qué es',
    manoPoner: 'Poner este tramo',
    manoFechas: 'Pon las dos fechas.',
    manoAlReves: 'La segunda fecha va después de la primera.',
    puestoTitulo: 'Lo que ya está puesto',
    puestoVacio: 'Todavía no hay ningún tramo puesto.',
    puestoAyuda: 'Para cambiar uno, pon otro tramo encima: vale el último.',
    faltanTitulo: 'Estos festivos nacionales no están en el fichero',
    faltanAyuda: 'Los calcula la app, sin red. Los autonómicos y locales los trae el fichero.',
    faltanAnadir: (n: number): string =>
      n === 1 ? 'Añadir ese festivo' : `Añadir esos ${n} festivos`,
    importado: (n: number): string =>
      n === 1 ? 'Una fecha puesta en el calendario.' : `${n} fechas puestas en el calendario.`,
  },
  days: {
    weekday: 'Día de colegio',
    weekend: 'Fin de semana',
    holiday: 'Festivo',
    vacation: 'Vacaciones',
    special: 'Día especial',
    sick: 'Día de enfermedad',
    paused: 'Día en pausa',
  },
  errors: {
    'not-configured': 'Todavía falta preparar la casa.',
    'unknown-member': 'No encuentro a esa persona.',
    'unknown-task': 'No encuentro esa tarea.',
    'unknown-reward': 'No encuentro eso en la tienda.',
    'unknown-purchase': 'No encuentro esa compra.',
    'not-allowed': 'Esto lo hace un adulto.',
    'steps-missing': 'Quedan pasos por hacer.',
    'photo-required': 'Esta tarea necesita una foto.',
    'already-done': 'Esto ya estaba hecho.',
    'not-marked': 'Todavía no está marcada como hecha.',
    'routine-cannot-pay': 'Una rutina no da monedas.',
    'chest-not-ready': 'Primero la rutina de la tarde, después el cofre.',
    'chest-already-open': 'El cofre de hoy ya está abierto.',
    'no-screen-today': 'Hoy no hay tablet.',
    'not-enough-coins': 'Te faltan monedas para eso.',
    'daily-cap-reached': 'Ya tienes todos los minutos extra de hoy.',
    'reward-limit-reached': 'Esto ya lo has comprado esta semana.',
    'reward-not-for-member': 'Esto no está en tu tienda.',
    'already-given': 'Esto ya te lo han dado. Ya no se puede deshacer.',
    invalid: 'Ahí falta un dato.',
  },
  common: {
    yes: 'Sí',
    no: 'No',
    pending: 'Pendiente',
    done: 'Hecho',
    close: 'Cerrar',
  },
} as const

/**
 * Words and shapes this app does not use.
 *
 * `label` rules out praising the person instead of the work ("eres un campeón").
 * `hedge` rules out the near-miss vocabulary, which reads as a broken promise.
 * `shout` rules out stacked exclamation marks.
 */
export const FORBIDDEN_PATTERNS: readonly { name: string; pattern: RegExp }[] = [
  {
    name: 'label',
    pattern: /\b(campe[oó]n|campeona|crack|fiera|m[aá]quina|genio|el mejor|la mejor)\b/i,
  },
  { name: 'hedge', pattern: /\b(casi|por poco|a punto de conseguirlo|qu[eé] pena)\b/i },
  { name: 'shout', pattern: /!!|¡¡/ },
  { name: 'irony', pattern: /\b(menos mal|ya era hora|por fin)\b/i },
  { name: 'shame', pattern: /\b(mal|fatal|desastre|vago|vaga|perezos[oa])\b/i },
]
